<template>
  <v-container>
    <avs-backButton
      v-if="!inDialog"
      backTo="CompanyVouchers"
    />
    <AvsSteppers
      :value="tab"
      @change="v => (tab = v)"
    >
      <template v-slot:headers>
        <AvsStep
          step="1"
          :rules="[() => isFormValid.informations]"
        >
          {{ $t("commons.informations") }}
          <small v-show="!isFormValid.informations">{{ $t("commons.validation_issues") }}</small>
        </AvsStep>
      </template>
      <template v-slot:items>
        <v-stepper-content step="1">
          <v-form
            ref="form-informations"
            v-model="isFormValid.informations"
            lazy-validation
          >
            <v-row dense>
            <v-col cols="12" md="12" class="mb-3 ml-1" style="margin-top: -10px;">
                <v-switch
                  v-model="staff.is_all_money"
                  :disabled="isDisable"
                  :label="$t('Bon en montant uniquement')"
                  inset
                  :hint="$t('Le bon ne sera pas lié à un produit, seul le montant sera pris en compte')"
                  persistent-hint
                />
                <v-alert
                  v-if="staff.is_all_money"
                  type="info"
                  color="secondary"
                  dense
                  outlined
                  class="mt-2"
                >
                  {{ $t('Le véhicule pourra prendre à la fois du Gazoil et du Super avec ce bon.') }}
                </v-alert>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="staff.bl_number"
                  :disabled="isDisabled(staff)"
                  :rules="isDisable ? [] : rules.bl_number"
                  dense
                  outlined
                  :label="$t('N° BL')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="staff.vehicle"
                  :disabled="isDisable"
                  :items="vehicles"
                  dense
                  :rules="rules.vehicles"
                  :item-text="formatVehicle"
                  item-value="id"
                  :label="$t('vehicule')"
                  attach
                  outlined
                ></v-autocomplete>
              </v-col>
              
              <v-col cols="12" md="6" v-if="!staff.is_all_money">
                <v-text-field
                  v-model.number="staff.purchase_liters"
                  :rules="isDisable ? [] : rules.purchase_liters"
                  clearable
                  dense
                  outlined
                  :label="$t('Nombre de litre')"
                  @input="onLitersInput"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="staff.purchase_amount"
                  :rules="staff.is_all_money ? [required, isNumber] : []"
                  dense
                  outlined
                  :label="$t('Montant Total')"
                  prefix="XOF "
                  @input="onAmountInput"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="staff.frequency"
                  :disabled="isDisable"
                  :items="frequencyItems"
                  :rules="rules.frequency"
                  dense
                  outlined
                  :label="$t('Frequence de Renouvellement')"
                ></v-autocomplete>
                <v-alert
                  v-if="isOneTime"
                  type="warning"
                  dense
                  outlined
                  class="mt-2"
                >
                  {{ $t('Ce bon ne pourra etre utilise qu\'une seule fois. Apres la premiere prise de carburant, le bon sera automatiquement cloture, meme s\'il reste du solde.') }}
                </v-alert>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="staff.recipient_company"
                  :disabled="isDisable || lockCompany"
                  :items="companies"
                  dense
                  :rules="lockCompany ? [] : rules.companies"
                  item-text="name"
                  item-value="pk"
                  :label="$t('Entreprise')"
                  attach
                  outlined
                  @change="updateProductsAndShops"
                />
              </v-col>
              <v-col cols="12" md="6" v-if="!staff.is_all_money">
                <v-autocomplete
                  v-model="staff.company_product"
                  :disabled="isDisable || products.length === 0 || vehicleAsProduct"
                  :items="products"
                  :rules="rules.products"
                  item-text="name"
                  item-value="id"
                  :label="$t('Produit')"
                  attach
                  dense
                  outlined
                  @change="onProductChange"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="staff.company_shop"
                  :disabled="isDisable || shops.length === 0"
                  :items="shops"
                  :rules="rules.shops"
                  item-text="name"
                  item-value="id"
                  :label="$t('Site')"
                  attach
                  dense
                  outlined
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="6" class="mb-3 ml-3" style="margin-top: -10px;">
                <v-switch
                  v-model="staff.allow_successive"
                  :disabled="!canAllowSuccessive || isDisable"
                  :label="$t('Autoriser la superposition des bons')"
                  inset
                  :hint="$t('Disponible uniquement si la fréquence est Non-renouvellable')"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="5" class="mb-3" style="margin-top: -10px;">
                <v-switch
                  v-model="staff.accumulate_remaining"
                  :disabled="canAllowSuccessive"
                  :label="$t('Accumuler le restant au renouvellement')"
                  inset
                  :hint="$t('Le solde non consommé sera reporté sur le prochain cycle')"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="6">
                <DatetimePicker
                  @input="handleInputOther"
                  v-model="staff.valid_until"
                  :min="minValidUntil"
                  :textFieldProps="{
                    placeholder: $t('commons.date.end_date'),
                    rules: rules.valid_until
                  }"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="staff.destination"
                  dense
                  outlined
                  :label="$t('Destination')"
                  :rules="isDisable ? [] : rules.destination"
                />
              </v-col>
            </v-row>

          </v-form>
        </v-stepper-content>

      </template>
      <template v-slot:actions>
        <v-card-actions class="mr-4 justify-end">
          <avs-saveButton
            @click="save"
          />
        </v-card-actions>
      </template>
    </AvsSteppers>

    <avs-CloseDialog
      v-model="closeDialogIsOpen"
      @cancel="handleCancel"
      @confirm="handleConfirm"
    />
    
  </v-container>
</template>

<script>
import _ from "lodash"
const noop = () => undefined
import { CompanyVouchersService, CompanyService, CompanyVehiclesService } from "@/services"
import { mkCompanyVoucher } from "@/utils/datamodels"


import { required, isNumber} from "@/utils/validator"
import { FREQUENCY_ITEMS } from "@/utils/consts"
import { stringToDate } from "@/utils/datetime"
export default {
  name: "VoucherCreateUpdate",
  components: {
    AvsStep: () => import("@/components/generics/general/AvsStep.vue"),
    AvsSteppers: () => import("@/components/generics/general/AvsSteppers.vue"),
    DatetimePicker: () => import("@/components/inputs/DatetimePicker.vue"),
    // DateInput,
  },
  props: {
    inDialog: {
      type: Boolean,
      default: false
    },
    inDialogParams: {
      type: Object,
      default: () => {}
    }
  },
  data: function () {
    return {
      required,
      isNumber,
      staffRef: null,
      staff: mkCompanyVoucher(),
      updatingField: null,
      selectedProductPrice: 0,
      vehicleAsProduct: false,
      tab: this.$route.params.tab ? this.$route.params.tab : 1,
      nextLabel: this.$t("commons.next"),
      loading: false,
      step: -1,
      companies: [],
      vehicles: [],
      products: [],
      shops: [],
      // totalAmount: 0,
      minReportDate: null,
      rules: {
        companies: [required],
        vehicles: [required],
        purchase_liters: [required, isNumber],
        valid_until: [required(this.$t("La date d'expiration est obligatoire."))],
        products: [required],
        shops: [],
        vin: [],
        destination: [],
        bl_number: [],
        frequency: [required]
      },
      isFormValid: {
        informations: false,
        budgeet: false,
        causeAndResolution: false
      },
      validateRequest: {
        budgeet: 0
      },                      
      isDisable: false,
      isClosed: false,
      closeDialogIsOpen: false,
      users: [],
    }
  },
  created() {
    this.fetchVoucher()
    this.fetchFieldTypes()
    this.fetchVehicleTypes()

    // fallback si le backend ne renvoie pas la clé
    if (this.staff.allow_successive === undefined) {
      this.$set(this.staff, 'allow_successive', false)
    }
  },
  methods: {
    save() {
      this.$refs["form-informations"].validate()
      this.$nextTick(() => {
        if (this.isFormValid.informations)
          if (this.staffExists) {
            this.updateVoucher()
          } else {
            this.createVoucher()
          }
        else {
          this.tab = 1
          const message = this.$t("messages.warning.all_required_info")
          this.send(message, "warning")
        }
      })
    },
    onLitersInput() {
      // Si le montant est en cours de mise à jour, on ne fait rien ici
      if (this.updatingField === 'amount') return;
      this.updatingField = 'liters';
      if (this.staff.product_price) {
        this.staff.purchase_amount = this.staff.purchase_liters * this.staff.product_price;
      }
      // Réinitialiser le flag dès que possible pour permettre d'autres mises à jour
      this.$nextTick(() => { this.updatingField = null; });
    },
    onAmountInput() {
      // Éviter la boucle infinie : si les litres sont en cours de mise à jour, ne rien faire
      if (this.updatingField === 'liters') return;
      this.updatingField = 'amount';
      if (this.staff.product_price) {
        // Calculer le nombre de litres et arrondir à 2 décimales
        this.staff.purchase_liters = Number((this.staff.purchase_amount / this.staff.product_price).toFixed(2));
      }
      this.$nextTick(() => { this.updatingField = null; });
    },
    formatVehicle(vehicle) {
      if (!vehicle) return "N/A"; // Vérifie si l'objet vehicle est null ou undefined

      let registrationNumber = vehicle.registration_number ? vehicle.registration_number : "N/A";
      let driverFirstName = vehicle.driver_first_name ? vehicle.driver_first_name : "";
      let driverLastName = vehicle.driver_last_name ? vehicle.driver_last_name : "";

      return `${registrationNumber} - ${driverLastName} ${driverFirstName}`.trim(); // Trim pour éviter les espaces inutiles
    },
    calculateTotalAmount() {
      const liters = parseFloat(this.staff.purchase_liters) || 0;
      const price = parseFloat(this.staff.product_price) || 0;
      this.staff.purchase_amount = liters * price;
    },
    handleInputOther() {
      this.$emit("input", this.staff);
    },
    /*fetchFieldTypes() {
      CompanyService.getAll().then(response => {
        this.companies = response;
        if (this.companies.length === 1) {
          this.staff.recipient_company = this.companies[0].pk;
          this.updateProductsAndShops(this.companies[0].pk);
          this.rules.companies = []; // Enlève la règle required si grisé
        }
      })
    },*/
    
    fetchFieldTypes() {
      CompanyService.getAll().then(response => {
        this.companies = response;

        if (this.lockCompany) {
          // Pas business + 1 seule entreprise -> auto-sélection + plus de "required"
          this.staff.recipient_company = this.companies[0].pk;
          this.updateProductsAndShops(this.staff.recipient_company);
          this.rules.companies = [];
        } else {
          // En "business" OU plusieurs entreprises -> ne rien pré-remplir, laisser "required"
          this.rules.companies = [required];
          if (this.isBusiness) {
            this.staff.recipient_company = null; // ⚠️ ne rien mettre par défaut
          }
        }
      })
    },
    fetchVehicleTypes() {
      CompanyVehiclesService.get(this, null, { all: true }).then(response => {
        this.vehicles = response.results || response
      })
    },
    tabIncrement() {
      return this.tab < 4 ? this.tab++ : this.$router.push({ name: "CompanyVouchers" })
    },
    handleClose() {
      return this.$router.push({ name: "CompanyVouchers" })
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg: msg, color: color })
    },
    isDisabled(item) {
      if (!item || !item.id) {
        return false;
      }

      const purchase  = Number(item.purchase_amount);
      const remaining = Number(item.remaining_amount);

      console.log('purchase:', purchase, 'remaining:', remaining);

      // true si les montants sont différents, false s'ils sont égaux
      const result = purchase !== remaining;

      this.isDisable = result; // si tu as besoin de garder un flag global
      return result;
    },
    getVoucher(id) {
      if (id) {
        let t = this
        CompanyVouchersService.get(this, {}, id).then(r => {
          console.log(r)
          t.staff = r
          t.staffRef = _.cloneDeep(t.staff)

          if (t.staff.recipient_company) {
            t.updateProductsAndShops(t.staff.recipient_company.pk);
          }

          // Recalculer le montant total
          t.calculateTotalAmount()
        })
      }
    },

    createVoucher() {
      let tmp = _.cloneDeep(this.staff)
      // sécurité : si pas Single -> pas de superposition
      if (!this.canAllowSuccessive) {
        tmp.allow_successive = false
      }
      this.loading = true
      CompanyVouchersService.create(this,  tmp)
        .then(response => {
          this.staff = response
          this.staffRef = _.cloneDeep(response)
          this.send(
            this.$t("Bon de commande enregistré"),
            "success"
          )
          this.$router.push({ name: "CompanyVouchers" })
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          } else {
            let message = ""
            for (const e in err.response.data) {
              message += "- " + e + " : " + err.response.data[e] + "\n"
            }
            this.$store.commit("app/message", {
              msg: message,
              color: "error"
            })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    updateVoucher() {
      const requests = [];

      // 🔥 Copier `staff` et convertir les objets en `id`
      let updatedStaff = _.cloneDeep(this.staff);
      // sécurité : si pas Single -> pas de superposition
      if (!this.canAllowSuccessive) {
        updatedStaff.allow_successive = false
      }
      updatedStaff.recipient_company = updatedStaff.recipient_company?.pk || updatedStaff.recipient_company;
      updatedStaff.company_product = updatedStaff.company_product?.id || updatedStaff.company_product;
      updatedStaff.company_shop = updatedStaff.company_shop?.id || updatedStaff.company_shop;

      console.log("🚀 Données envoyées à l'API:", updatedStaff);

      // Vérifier si des modifications ont été faites avant d'envoyer la requête
      if (!_.isEqual(_.omit(updatedStaff), _.omit(this.staffRef))) {
        requests.push(CompanyVouchersService.update(this, updatedStaff.id, _.omit(updatedStaff)));
      }

      if (requests.length > 0) {
        this.loading = true;
        Promise.all(requests)
          .then(() => {
            this.send(
              this.$t("Bon de commande modifié"),
              "success"
            );
            this.$router.push({ name: "CompanyVouchers" });
          })
          .catch(err => {
            let message = "";
            if (err.response && err.response.data) {
              for (const field in err.response.data) {
                message += `- ${field} : ${err.response.data[field]}\n`;
              }
            } else {
              message = this.$t("messages.error.unknown");
            }
            this.send(message, "error");
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.send(this.$t("messages.success.no_updated"), "info");
      }
    },
    fetchVoucher() {
      // Load Voucher
      this.getVoucher(this.id)
    },
    onProductChange(newProductId) {
      console.log("🔍 Produit sélectionné ID:", newProductId);
      let selectedProduct = this.products.find(p => p.id === newProductId);
      
      if (selectedProduct) {
        console.log("✅ Produit trouvé:", selectedProduct);
        this.staff.product_price = selectedProduct.price;
      } else {
        console.warn("⚠️ Aucun produit trouvé !");
        this.staff.product_price = 0;
      }

      this.calculateTotalAmount();
    },

    updateProductsAndShops(companyId) {
      if (!companyId) {
        console.warn("⚠️ Aucun companyId trouvé !");
        this.products = [];
        this.shops = [];
        return;
      }

      console.log("🚀 Chargement des produits et shops pour l'entreprise ID:", companyId);

      // Vérifier si l'entreprise existe bien
      const selectedCompany = this.companies.find(company => company.pk === companyId);
      if (selectedCompany) {
        this.products = selectedCompany.products.map(product => ({
          ...product,
          price: product.price, // Ajoute le prix pour l'utiliser dans le calcul
        }));
        // this.shops = selectedCompany.shops || [];
        this.shops = [{ id: "", name: "Tout le réseau" }, ...selectedCompany.shops];

        console.log("✅ Produits chargés:", this.products);
        console.log("✅ Shops chargés:", this.shops);

        // Vérifier si les anciens produits et shops existent encore
        if (!this.products.find(p => p.id === this.staff.company_product?.id)) {
          console.warn("⚠️ Le produit sélectionné n'existe plus !");
          this.staff.company_product = null;
        }
        if (!this.shops.find(s => s.id === this.staff.company_shop?.id)) {
          console.warn("⚠️ Le shop sélectionné n'existe plus !");
          this.staff.company_shop = null;
        }

        // Vérifier si un produit est sélectionné et mettre à jour le prix
        // const selectedProduct = this.products.find(p => p.id === this.staff.company_product?.id);
        // this.selectedProductPrice = selectedProduct ? selectedProduct.price : 0;

        const selectedProduct = this.products.find(p => p.id === this.staff.company_product?.id);
        if (selectedProduct) {
          this.staff.product_price = selectedProduct.price;  // 🔥 Stocker le prix directement dans staff
        } else {
          this.staff.product_price = 0;
        }

        // Recalculer le montant
        this.calculateTotalAmount();
      } else {
        console.warn("⚠️ L'entreprise n'a pas été trouvée dans la liste !");
      }
    },
    handleError(msg) {
      // Handle message display
      this.$store.commit("app/message", { msg, color: "error" })
    },
    handleSuccess(isSaved, nextTab, postProcess = noop, message = null) {
      // Handle message display
      const msg_success = message ? message : this.$t("messages.success.operation")
      const data = isSaved
        ? { msg: msg_success, color: "success" }
        : { msg: this.$t("messages.success.no_updated"), color: "info" }
      this.$store.commit("app/message", data)

      // Handle next display
      this.tab = nextTab

      // Postprocess
      postProcess()
    },
    handleConfirm() {
      CompanyVouchersService.close(this, this.staff.id)
        .then(r => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.closed", {
              entity: this.$tc("categories.staff.name", 1)
            }),
            color: "success"
          })
          this.closeDialogIsOpen = false
          this.isClosed = true
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
      this.closeDialogIsOpen = false
    },
    handleCancel() {
      this.closeDialogIsOpen = false
    }
  },
  watch: {
    tab(newValue, oldValue) {
      if (oldValue === 1) {
        this.save()
      } 
    },
    'staff.product_price'(newPrice) {
      if (newPrice) {
        this.staff.purchase_amount = this.staff.purchase_liters * newPrice;
      }
    },
    "staff.recipient_company": {
      handler(newCompany) {
        if (newCompany && newCompany.id) {
          console.log("📌 Changement de société détecté:", newCompany.id);
          this.updateProductsAndShops(newCompany.id);
        } else {
          console.warn("⚠️ Aucune société sélectionnée !");
        }
      },
      immediate: true // Exécute dès le montage du composant
    },
    /*"staff.recipient_company": {
      handler(newCompany) {
        const pk = typeof newCompany === 'object' ? (newCompany.pk || newCompany.id) : newCompany
        if (pk) {
          console.log("📌 Changement de société détecté:", pk);
          this.updateProductsAndShops(pk);
        } else {
          console.warn("⚠️ Aucune société sélectionnée !");
        }
      },
      immediate: true // Exécute dès le montage du composant
    },*/
    "staff.is_all_money"(val) {
      if (val) {
        // Quand all money, on réinitialise produit et litrage
        this.staff.company_product = null
        this.staff.purchase_liters = ""
        this.staff.product_price = 0
      }
    },
    "staff.frequency"(val) {
      // Sécurité : dès que ce n’est pas Single, on force false
      if (!this.canAllowSuccessive && this.staff.allow_successive) {
        this.staff.allow_successive = false
      }
      // Si fréquence Single -> pas d’accumulation (pas de renouvellement)
      if (this.canAllowSuccessive && this.staff.accumulate_remaining) {
        this.staff.accumulate_remaining = false
      }
    },
    selectedProduct(newProduct) {
      if (newProduct) {
        console.log("📌 Changement détecté: Produit ID", newProduct.id);
        this.staff.product_price = newProduct.price;
        this.calculateTotalAmount();
      } else {
        console.warn("⚠️ Aucun produit détecté !");
        this.staff.product_price = 0;
      }
    },
    "staff.company_shop": {
      handler(newShop) {
        if (newShop && newShop.id) {
          console.log("📌 Changement de shop détecté:", newShop.id);
        } else {
          console.warn("⚠️ Aucun shop sélectionné !");
        }
      },
      immediate: true
    },
    'staff.vehicle': {
      handler(newVehicleId) {
        this.vehicleAsProduct = false
        const selectedVehicle = this.vehicles.find(v => v.id === newVehicleId);
        if (selectedVehicle) {
          console.log("🚗 Véhicule sélectionné :", selectedVehicle);
          if (selectedVehicle.product) {
            const linkedProduct = this.products.find(p => p.id === selectedVehicle.product);
            if (linkedProduct) {
              console.log("🔗 Produit lié trouvé :", linkedProduct);
              this.vehicleAsProduct = true
              this.staff.company_product = linkedProduct.id;
              this.staff.product_price = linkedProduct.price;
              this.calculateTotalAmount();
            } else {
              
              console.warn("⚠️ Produit lié non trouvé dans la liste !");
            }
          } else {
            console.warn("⚠️ Pas de produit lié à ce véhicule !");
          }
        }
      },
      immediate: true
    }

  },
  computed: {
    id() {
      return this.inDialog ? this.inDialogParams?.id : this.$route.params.id || null
    },
    staffExists() {
      return this.staff.id
    },
    isBusiness() {
      return (this.$store.getters["app/getCompany"]?.company_type || "").toLowerCase() === "business"
    },
    /*lockCompany() {
      // On auto-sélectionne + verrouille uniquement si pas "business" et une seule entreprise
      return this.companies.length === 1 && !this.isBusiness
    },*/
    isPrincipal() {
      return !!(this.$store.getters["app/getCompany"]?.is_principal)
    },
    lockCompany() {
      // 🔒 Verrouiller si société principale OU (mono-entreprise et pas "business")
      return this.isPrincipal || (this.companies.length === 1 && !this.isBusiness)
    },
    company() {
      return this.$store.state.app.company
    },
    selectedProduct() {
      return this.products.find(p => p.id === this.staff.company_product);
    },
    frequencyItems() {
      return FREQUENCY_ITEMS
    },
    isOneTime() {
      const raw = this.staff?.frequency
      const v = typeof raw === 'string'
        ? raw
        : (raw?.value || raw?.ident || '')
      return String(v).toLowerCase() === 'onetime'
    },
    canAllowSuccessive() {
      const raw = this.staff?.frequency
      const v = typeof raw === 'string'
        ? raw
        : (raw?.value || raw?.ident || '')
      return ['single', 'onetime'].includes(String(v).toLowerCase())
    },
    minValidUntil() {
      // En mode édition, empêcher de choisir une date antérieure à la date actuelle du bon
      if (this.staffRef && this.staffRef.valid_until) {
        return stringToDate(this.staffRef.valid_until)
      }
      return undefined
    },
  }
}
</script>

<style lang="scss">
.custom-container {
  display: flex;
  flex-direction: column;
  height: 80vh;
}
.stepper-item {
  flex: 1 1 auto;
  overflow-y: auto;
}
.ck.ck-editor__main > .ck-editor__editable {
  height: 130px;
}
.ck.ck-balloon-panel.ck-powered-by-balloon .ck.ck-powered-by {
  display: none !important;
}
.ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
.ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

.ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar,
.ck.ck-editor__top .ck-sticky-panel .ck-toolbar.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.ck-editor__editable {
  color: rgba(0, 0, 0, 0.87) !important;
}
</style>
